// Types shared between the React app and the Cloudflare Worker.
// Single source of truth for the API contract.

export type Currency = 'CRC' | 'USD';

export type Service = {
  /** Internal id, e.g. "gineco-ultra-pap" */
  id: string;
  /** Display name, e.g. "Consulta Ginecológica + Ultrasonido + PAP" */
  name: string;
  price: number;
  minPrice?: boolean;
  currency: Currency;
  durationMinutes: number;
  /** Sikumed ProviderID */
  providerId?: number,
  /** Sikumed ReasonID */
  reasonId: number;
  /** Sikumed AgendaID */
  agendaId: number;
  /** cuando el servicio se arma escogiendo zonas, acá va su tabla */
  porZonas?: PorZonas;
  /** las opciones del tarifario de este servicio, cuando hay más de una */
  variantes?: Variante[];
};

export type Doctor = {
  id: string;
  name: string;
  specialty: string;
  /** Sikumed ProviderID */
  providerId: number;
  services: Service[];
  /**
   * "Look busy" knob: percentage (0-100) of each day's available slots to
   * deterministically hide, making the agenda appear busier. Omit or 0 to
   * show all real availability. Marketing-tunable per doctor.
   */
  lookBusyPercent?: number;
};

export type DaySlots = {
  /** YYYY-MM-DD in clinic local timezone (America/Costa_Rica) */
  date: string;
  /** Start times in HH:mm format */
  slots: string[];
};

export type MonthAvailability = {
  /** YYYY-MM */
  month: string;
  /** Only days with at least one open slot */
  days: DaySlots[];
};

/**
 * Identification type. Maps 1:1 to Sikumed's IdentificationTypeID catalog:
 *   cedula     → 1  Cédula de Identidad
 *   extranjero → 2  Extranjero (passport / foreign)
 *   dimex      → 3  DIMEX (residency)
 *   menor      → 4  Menor de Edad
 *
 * TSE lookup is only attempted for `cedula` (the only one with a 9-digit
 * CR national ID in the voter rolls).
 */
export type IdType = 'cedula' | 'extranjero' | 'dimex' | 'menor';

/**
 * Validation rules per id type, shared by the web form and the Worker so
 * client and server stay in sync.
 * - cedula: exactly 9 numeric digits (CR national ID)
 * - extranjero: passport / foreign — alphanumeric, up to 20
 * - dimex: residency — numeric, up to 20
 * - menor: minor without cédula — up to 20
 */
export const ID_RULES: Record<IdType, { min: number; max: number; numeric: boolean }> = {
  cedula: { min: 9, max: 9, numeric: true },
  extranjero: { min: 5, max: 20, numeric: false },
  dimex: { min: 5, max: 20, numeric: true },
  menor: { min: 5, max: 20, numeric: false },
};

/** Validate an id number against its type's rules. Returns null if valid. */
export function validateIdNumber(idType: IdType, raw: string): string | null {
  const value = raw.trim();
  const rules = ID_RULES[idType];
  if (idType === 'cedula') {
    return /^\d{9}$/.test(value) ? null : 'cedula_format';
  }
  if (value.length < rules.min) return 'too_short';
  if (value.length > rules.max) return 'too_long';
  if (rules.numeric && !/^\d+$/.test(value)) return 'not_numeric';
  return null;
}

export type PatientInput = {
  idType: IdType;
  firstName: string;
  /** Primer apellido → Sikumed LastName */
  lastName: string;
  /** Segundo apellido → Sikumed SecondLastName. May be empty for foreigners. */
  secondLastName: string;
  /** Cédula or other national ID */
  idNumber: string;
  email: string;
  /** E.164 format preferred: +50688887777 */
  phone: string;
};

/** Response from the Worker's /lookup-cedula endpoint. */
export type CedulaLookupResponse =
  | { found: true; firstName: string; lastName: string; secondLastName: string }
  | { found: false }
  | { error: 'rate_limited' | 'invalid' | 'upstream' };

/**
 * Attribution data captured on landing. Persisted in sessionStorage
 * through the funnel and submitted with the booking.
 */
export type TrafficSource = {
  /** utm_source — e.g. "facebook", "google", "instagram", "direct" */
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  /** Facebook click ID — required for Meta CAPI matching */
  fbclid?: string;
  /** Facebook browser ID — first-party cookie set by Pixel */
  fbp?: string;
  /** Google Ads click ID */
  gclid?: string;
  /** TikTok click ID */
  ttclid?: string;
  /** Cookie _ttp que pone el píxel de TikTok, para emparejar desde el servidor */
  ttp?: string;
  /** document.referrer at landing */
  referrer?: string;
  /** Full URL of the landing page — required by Meta CAPI */
  landingUrl?: string;
  /** ISO timestamp of first landing */
  landedAt?: string;
};

/** Una opción del tarifario dentro de un mismo servicio del calendario: la
    colocación de Mirena y la de T de plata son la misma cita, con precio
    distinto. Salen de la base, no del código. */
export type Variante = {
  nombre: string;
  precio: number | null;
  moneda: Currency;
  minutos: number | null;
  nota: string | null;
};

/** Una zona de depilación: su tamaño manda el precio y el tiempo. */
export type Zona = {
  id: string;
  nombre: string;
  tamano: 'pequena' | 'mediana' | 'grande';
  /** zonas que esta ya cubre: al marcarla se desmarcan y se bloquean */
  incluye?: string[];
  /** si estas quedan todas marcadas, se juntan en esta */
  seArmaCon?: string[];
};

/** Servicios que se arman escogiendo zonas (depilación láser). */
export type PorZonas = {
  /** minutos que se suman siempre, para la valoración */
  minutosBase: number;
  /** lo que hay que aclarar del precio: «por paquete de 6 sesiones» */
  nota?: string;
  tamanos: Record<'pequena' | 'mediana' | 'grande', { precio: number; minutos: number }>;
  /** el motivo de Sikumed lo pone la zona más grande que se haya escogido */
  motivoPorTamano: Record<'pequena' | 'mediana' | 'grande', number>;
  zonas: Zona[];
};

export type BookingRequest = {
  doctorId: string;
  serviceId: string;
  /** YYYY-MM-DD */
  date: string;
  /** HH:mm */
  time: string;
  patient: PatientInput;
  /** Attribution — optional; populated when the user came from an ad */
  source?: TrafficSource;
  /** Cloudflare Turnstile token (anti-bot). Required in production. */
  turnstileToken?: string;
  /** Zonas escogidas, cuando el servicio se arma por zonas (depilación láser). */
  zonas?: string[];
  /** Nombre de la opción escogida, cuando el servicio tiene varias. */
  variante?: string;
};

export type BookingErrorCode =
  | 'slot_taken'
  | 'validation'
  | 'sikumed_error'
  | 'rate_limited'
  | 'captcha';

export type BookingResponse =
  | { ok: true; bookingId: string; confirmationCode: string }
  | { ok: false; code: BookingErrorCode; message: string };
